import {
  GRANTABLE_PERMISSIONS,
  type GrantablePermission,
  type Role,
  type UserSummary,
} from "@pokifolio/contracts";
import { useState } from "react";

import { useApi } from "../../app/api-context";
import { ErrorAlert } from "../../components/ui/alert";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Select } from "../../components/ui/input";
import { PERMISSION_LABELS } from "./permission-labels";
import { useUserMutation } from "./users-queries";

interface UserRowProps {
  readonly user: UserSummary;
  readonly isSelf: boolean;
}

/** One person in the users table, with role, status and permission controls. */
export function UserRow({ user, isSelf }: UserRowProps) {
  const api = useApi();
  const [confirming, setConfirming] = useState(false);
  const [editingPermissions, setEditingPermissions] = useState(false);
  const [draft, setDraft] = useState<GrantablePermission[]>(user.extraPermissions);

  const changeRole = useUserMutation((role: Role) => api.updateUser(user.id, { role }));
  const changeApproval = useUserMutation((requiresPurchaseApproval: boolean) =>
    api.updateUser(user.id, { requiresPurchaseApproval }),
  );
  const deactivate = useUserMutation(() => api.deactivateUser(user.id));
  const reactivate = useUserMutation(() => api.reactivateUser(user.id));
  const savePermissions = useUserMutation((permissions: GrantablePermission[]) =>
    api.setUserPermissions(user.id, permissions),
  );

  const error =
    changeRole.error ?? changeApproval.error ?? deactivate.error ?? reactivate.error ?? savePermissions.error;
  const active = user.status === "active";

  function togglePermission(permission: GrantablePermission) {
    setDraft((current) =>
      current.includes(permission) ? current.filter((p) => p !== permission) : [...current, permission],
    );
  }

  return (
    <>
      <tr className="border-t border-border align-top">
        <td className="px-4 py-3">
          <p className="font-medium text-foreground">
            {user.name}
            {isSelf && <span className="ml-2 text-xs font-normal text-muted-foreground">(you)</span>}
          </p>
          <p className="text-sm text-muted-foreground">{user.email}</p>
          {user.mustChangePassword && (
            <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">Has not set a password yet</p>
          )}
        </td>
        <td className="px-4 py-3">
          <Select
            aria-label={`Role for ${user.name}`}
            className="h-9 w-36"
            value={user.role}
            disabled={isSelf || changeRole.isPending}
            onChange={(e) => changeRole.mutate(e.target.value === "admin" ? "admin" : "employee")}
          >
            <option value="employee">Employee</option>
            <option value="admin">Admin</option>
          </Select>
        </td>
        <td className="px-4 py-3">
          <Badge tone={active ? "success" : "danger"}>{active ? "Active" : "Deactivated"}</Badge>
        </td>
        <td className="px-4 py-3 text-sm">
          {user.role !== "admin" && (
            <label className="mb-2 flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-0.5"
                aria-label={`Must ask admin before buying (${user.name})`}
                checked={user.requiresPurchaseApproval}
                disabled={changeApproval.isPending}
                onChange={(e) => changeApproval.mutate(e.target.checked)}
              />
              <span>
                Must ask admin before buying
                <span className="block text-xs text-muted-foreground">
                  {user.requiresPurchaseApproval
                    ? "Purchases need your approval first."
                    : "Can buy without asking."}
                </span>
              </span>
            </label>
          )}
          {user.role === "admin" ? (
            <span className="text-muted-foreground">All permissions; never needs approval</span>
          ) : editingPermissions ? (
            <fieldset className="flex flex-col gap-1.5">
              <legend className="sr-only">Extra permissions for {user.name}</legend>
              {GRANTABLE_PERMISSIONS.map((permission) => (
                <label key={permission} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={draft.includes(permission)}
                    onChange={() => togglePermission(permission)}
                  />
                  {PERMISSION_LABELS[permission]}
                </label>
              ))}
              <div className="mt-1 flex gap-2">
                <Button
                  size="sm"
                  disabled={savePermissions.isPending}
                  onClick={() =>
                    savePermissions.mutate(draft, { onSuccess: () => setEditingPermissions(false) })
                  }
                >
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setDraft(user.extraPermissions);
                    setEditingPermissions(false);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </fieldset>
          ) : (
            <div className="flex flex-col items-start gap-1">
              {user.extraPermissions.length === 0 ? (
                <span className="text-muted-foreground">None</span>
              ) : (
                user.extraPermissions.map((p) => <span key={p}>{PERMISSION_LABELS[p]}</span>)
              )}
              <Button
                size="sm"
                variant="ghost"
                className="-ml-3"
                aria-label={`Edit extra permissions for ${user.name}`}
                onClick={() => setEditingPermissions(true)}
              >
                Edit
              </Button>
            </div>
          )}
        </td>
        <td className="px-4 py-3 text-right">
          {isSelf ? null : active ? (
            confirming ? (
              <div className="flex flex-col items-end gap-2">
                <p className="text-xs text-muted-foreground">Sign {user.name} out and block access?</p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={deactivate.isPending}
                    onClick={() => deactivate.mutate(undefined, { onSettled: () => setConfirming(false) })}
                  >
                    Deactivate
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                size="sm"
                variant="secondary"
                aria-label={`Deactivate ${user.name}`}
                onClick={() => setConfirming(true)}
              >
                Deactivate
              </Button>
            )
          ) : (
            <Button
              size="sm"
              variant="secondary"
              aria-label={`Reactivate ${user.name}`}
              disabled={reactivate.isPending}
              onClick={() => reactivate.mutate(undefined)}
            >
              Reactivate
            </Button>
          )}
        </td>
      </tr>
      {error !== null && (
        <tr>
          <td colSpan={5} className="px-4 pb-3">
            <ErrorAlert error={error} />
          </td>
        </tr>
      )}
    </>
  );
}
